import React from 'react';
import { Loader, Modal } from '@mantine/core';
import PropTypes from 'prop-types';
import {
  useDashboardCookies,
  useMediaQueries,
  useMediaQueryIndex
} from '../../helpers/hooks';
import { getResponsiveStyle as rs } from '../../helpers/styles';
import AppFlexbox from './AppFlexbox';
import AppTitle from './AppTitle';
import AppText from './AppText';
import AppStack from './AppStack';
import FormSection from './FormSection';

const ResponsiveModal = ({
  isOpen,
  onClose,
  title,
  size,
  isLoading,
  children,
  sidePadding,
  noCloseButton,
  formSectionProps,
  bodyProps,
  isDrawer,
  isNewModalStyle,
  ...rest
}) => {
  const mqIndex = useMediaQueryIndex();
  const { isTabletOrSmaller } = useMediaQueries();
  const { isClassicView } = useDashboardCookies();

  const modalSidePadding = sidePadding ?? 40;
  return isClassicView && !isNewModalStyle ? (
    <Modal.Root
      centered
      onClose={onClose}
      opened={isOpen}
      styles={{
        header: {
          padding: rs(
            [
              '20px 20px 0px 20px',
              `20px 30px 0px 30px`,
              `20px ${modalSidePadding}px 0px ${modalSidePadding}px`
            ],
            mqIndex
          )
        },
        title: { flex: 1, padding: 0 },
        inner: { padding: 0 },
        body: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          padding: rs(
            [
              20,
              `20px 30px 40px 30px`,
              `20px ${modalSidePadding}px 40px ${modalSidePadding}px`
            ],
            mqIndex
          )
        },
        root: { flex: 1 },
        content: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          maxWidth: rs(['100%', '100%', size ?? 600], mqIndex),
          minHeight: rs(['100%', '100%', 'unset'], mqIndex)
        }
      }}
      withinPortal
      {...rest}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          {typeof title === 'string' ? (
            <AppTitle order={2}>{title}</AppTitle>
          ) : (
            title
          )}

          {!noCloseButton && <Modal.CloseButton />}
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <AppFlexbox style={{ padding: 40, margin: 'auto' }}>
              <Loader color="dark" size="xl" />
            </AppFlexbox>
          ) : !formSectionProps ? (
            children
          ) : (
            <FormSection {...formSectionProps}>{children}</FormSection>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  ) : (
    <Modal.Root
      centered
      onClose={onClose}
      opened={isOpen}
      withinPortal
      {...rest}
      styles={{
        ...rest.styles,
        header: {
          padding: 16,
          minHeight: 60,
          backgroundColor: '#f3f3f3',
          borderBottom: 'solid 1px #dee2e6',
          ...rest.styles?.header
        },
        title: { flex: 1, padding: 0, ...rest.styles?.title },
        inner: {
          padding: 0,
          height: '100%',
          top: isDrawer || isTabletOrSmaller ? 'unset' : 0,
          alignItems: isDrawer || isTabletOrSmaller ? 'end' : 'center',
          ...rest.styles?.inner
        },
        body: {
          maxHeight: 'calc(100% - 60px)',
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          padding: 0,
          ...rest.styles?.body
        },
        root: { flex: 1, ...rest.styles?.root },
        content: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          maxWidth: size ?? 600,
          minHeight: 'unset',
          borderRadius: 20,
          ...(isDrawer || isTabletOrSmaller
            ? {
                maxHeight: '100%',
                maxWidth: '100%',
                borderRadius: 0
              }
            : {}),
          ...rest.styles?.content
        }
      }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          {typeof title === 'string' ? (
            <AppText style={{ fontSize: 16, fontWeight: 650 }}>{title}</AppText>
          ) : (
            title
          )}

          {!noCloseButton && <Modal.CloseButton />}
        </Modal.Header>
        <Modal.Body>
          {isLoading ? (
            <AppFlexbox style={{ padding: 40, margin: 'auto' }}>
              <Loader color="dark" size="xl" />
            </AppFlexbox>
          ) : !formSectionProps ? (
            <AppStack
              {...bodyProps}
              style={{
                gap: 16,
                padding: 16,
                flex: 1,
                height: '100%',
                overflow: 'auto',
                ...bodyProps?.style
              }}
            >
              {children}
            </AppStack>
          ) : (
            <FormSection
              {...formSectionProps}
              isNewModalStyle={isNewModalStyle}
            >
              {children}
            </FormSection>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};

ResponsiveModal.propTypes = {
  bodyProps: PropTypes.object,
  children: PropTypes.any,
  formSectionProps: PropTypes.object,
  isDrawer: PropTypes.bool,
  isLoading: PropTypes.bool,
  isNewModalStyle: PropTypes.bool,
  isOpen: PropTypes.bool,
  noCloseButton: PropTypes.bool,
  onClose: PropTypes.func,
  sidePadding: PropTypes.number,
  size: PropTypes.number,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
};

export default ResponsiveModal;
